import { Directive, Input, OnInit } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { FieldComponent } from '~app/components/shared/forms/field/field.component';
import { LocationService, PersonService } from '~app/services';
import { PersonAttributesSlim } from '~app/models/person';

/**
 * Best-effort USDA detection used by the people-usda / people-non-usda
 * subtypes. The slim person's `isUSDAUser` is a virtual; the API has been
 * populating it for these dropdowns to date, but the contract is optional
 * (`isUSDAUser?: boolean` on PersonAttributesSlim) so falling back to parsing
 * the email out of displayNameDropdown protects against a payload change
 * silently emptying the dropdown.
 */
function isUsdaSlim(person: PersonAttributesSlim): boolean {
  if (typeof person?.isUSDAUser === 'boolean') return person.isUSDAUser;
  const m = person?.displayNameDropdown?.match(/\(([^()]+)\)\s*$/);
  if (m) return m[1].trim().toLowerCase().endsWith('@usda.gov');
  return false;
}

@Directive({
    selector: 'ciao-form-field[subtype][type=dropdown], ciao-form-field[subtype][type=combobox]',
    standalone: false
})
export class DropdownSubtypeDirective implements OnInit {
  @Input('subtype') subtype: string;
  @Input() selectOptions$: Observable<SelectOption[]>;
  constructor(
    private host: FieldComponent,
    private personService: PersonService,
    private locationService: LocationService
  ) {}
  ngOnInit() {
    switch (this.subtype) {
      case 'people':
        this.host.selectOptions$ = this.personService.allPeople.pipe(
          map((people) =>
            people.map((person) => ({
              value: person,
              label: person.displayNameDropdown,
              // Slim person lacks AssignedRoles; the badge resolves guest
              // status by id through GuestRoleService.
              badgePerson: { id: person.id },
            }))
          )
        );
        break;
      case 'people-usda':
        // USDA-only pool for v2.4 Point of Contact and the Supervisor /
        // Team Member dropdowns. isUsdaSlim falls back to email-parsing when
        // the precomputed flag is missing so a payload regression can't
        // silently empty the dropdown.
        this.host.selectOptions$ = this.personService.allPeople.pipe(
          map((people) => people.filter((person) => isUsdaSlim(person))),
          map((people) =>
            people.map((person) => ({
              value: person,
              label: person.displayNameDropdown,
            }))
          )
        );
        break;
      case 'people-non-usda':
        // Inverse of `people-usda` — feeds the v2.4 Dispatcher and Guest
        // dropdowns where only non-USDA users are valid selections.
        this.host.selectOptions$ = this.personService.allPeople.pipe(
          map((people) => people.filter((person) => !isUsdaSlim(person))),
          map((people) =>
            people.map((person) => ({
              value: person,
              label: person.displayNameDropdown,
            }))
          )
        );
        break;
      case 'countyCodes':
        this.host.selectOptions$ = this.locationService.allCounties$.pipe(
          map((counties) =>
            counties.map((county) => ({
              value: county.id,
              label: county.id,
            }))
          )
        );
        break;
      default:
        break;
    }
  }
}

type SelectOption =
  | string
  | {
      label: string;
      value: string | Object;
    };
